import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import {
  CollectionUtil,
  DataContextBuilder,
  ElderTableComponent,
  IDataContext,
  LocalListDataSource,
} from '@elderbyte/ngx-starter'
import { MaterialInfo, TracedMaterial } from '../traced-material'
import { GroupedTracedMaterials } from './grouped-traced-materials'
import { BatchTracingConf } from '../../conf/batch-tracing-conf'
import { TracedMaterialUpdate } from '../../traced-material-update'
import { BatchTracingSpec } from '../../spec/batch-tracing-spec'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { MatButton } from '@angular/material/button'
import { BatchTracingConfDetailComponent } from '../../conf/batch-tracing-conf-detail/batch-tracing-conf-detail.component'

import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import { FormsModule } from '@angular/forms'

@Component({
  selector: 'app-traced-material-grouped',
  templateUrl: './traced-material-grouped.component.html',
  styleUrls: ['./traced-material-grouped.component.scss'],
  imports: [
    FormsModule,
    ElderTableComponent,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    BatchTracingConfDetailComponent,
    MatButton,
    ElderTranslatePipe,
  ],
})
export class TracedMaterialGroupedComponent implements OnInit, OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly localDataSrc: LocalListDataSource<GroupedTracedMaterials>
  public readonly data: IDataContext<GroupedTracedMaterials>

  @Output()
  public updates = new EventEmitter<TracedMaterialUpdate[]>()

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.localDataSrc = LocalListDataSource.empty('specId')

    this.data = DataContextBuilder.start<GroupedTracedMaterials>()
      .reloadOnLocalChanges()
      .build(this.localDataSrc)
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    this.data.start()
  }

  public ngOnDestroy(): void {
    if (this.data) {
      this.data.close()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set tracedMaterials(materials: TracedMaterial[]) {
    this.localDataSrc.data = this.groupMaterialsBySpec(materials)
    this.data.refresh()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onConfirmTracingConfig(): void {
    const updates: TracedMaterialUpdate[] = []
    this.data.snapshot.data.forEach((group) =>
      updates.push(...this.buildTracedMaterialUpdates(group))
    )
    this.updates.emit(updates)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private groupMaterialsBySpec(materials: TracedMaterial[]) {
    const groupable = materials.filter((m) => m?.spec?.serialNumber?.regex?.length === 0)
    const ungroupable = materials.filter((m) => m?.spec?.serialNumber?.regex?.length > 0)

    const groups: GroupedTracedMaterials[] = []
    CollectionUtil.groupByKey(groupable, (m) => m?.spec?.id + m?.conf?.vendor).forEach(
      (groupedMaterials: TracedMaterial[], _: string) =>
        groups.push(this.buildGroupedTracedMaterials(groupedMaterials))
    )

    ungroupable.forEach((m) => groups.push(this.buildGroupedTracedMaterials([m])))

    return groups
  }

  private buildGroupedTracedMaterials(groupedMaterials: TracedMaterial[]): GroupedTracedMaterials {
    const specId = groupedMaterials.map((m) => m?.spec?.id).find((id) => id != null)
    return new GroupedTracedMaterials(
      specId,
      groupedMaterials,
      this.determineCommonMaterialInfo(groupedMaterials),
      this.determineCommonSpec(groupedMaterials),
      new BatchTracingConf(
        specId,
        this.determineGroupSerialNumber(groupedMaterials),
        this.determineGroupVendor(groupedMaterials)
      )
    )
  }

  private determineCommonMaterialInfo(groupedMaterials: TracedMaterial[]): MaterialInfo[] {
    const matInfos = groupedMaterials.map((m) => m.materialInfo)

    const distinctInfos: MaterialInfo[] = []
    CollectionUtil.groupByKey(matInfos, (info) => info?.path + info?.id).forEach(
      (infos: MaterialInfo[], _: string) => distinctInfos.push(infos[0])
    )

    return distinctInfos
  }

  private determineCommonSpec(groupedMaterials: TracedMaterial[]): BatchTracingSpec {
    return groupedMaterials.map((m) => m?.spec).find((s) => s != null)
  }

  private determineGroupSerialNumber(groupedMaterials: TracedMaterial[]): string {
    return groupedMaterials?.length === 1 ? groupedMaterials[0]?.conf?.serialNumber : ''
  }

  private determineGroupVendor(groupedMaterials: TracedMaterial[]): string {
    const vendor = groupedMaterials.map((m) => m.conf.vendor).find(() => true)
    const sameVendor = groupedMaterials.every((m) => m.conf.vendor === vendor)

    return sameVendor && vendor ? vendor : ''
  }

  private buildTracedMaterialUpdates(group: GroupedTracedMaterials): TracedMaterialUpdate[] {
    return group.tracedMaterials.map(
      (tm) =>
        new TracedMaterialUpdate(tm.itemInfo.id, tm.workCenterId, tm.materialInfo.path, group.conf)
    )
  }
}
